import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { UserRole } from "@/lib/rbac";
import { apiLogin, apiGetMe, apiLogout } from "@/lib/api/auth-api";
import type { ApiStoreDTO, LoginApiResponse } from "@/lib/api/auth-api";
import { setAuthToken, getAuthToken, isApiReachable, ENABLE_MOCK_FALLBACK, ApiError } from "@/lib/api/http-client";
import { getMyRole, getMyStores } from "@/lib/api/stores-api";
import { useStoreStore } from "./store-store";
import type { StoreTier } from "@/lib/billing/tier";

// ============================================================
// Types
// ============================================================

export interface StoreAccess {
  storeId: string;
  storeName: string;
  role: UserRole;
  tier: StoreTier;
  category?: string;
  description?: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  avatar: string;
  role: UserRole;
  stores: StoreAccess[];
  hasPin: boolean;
  pinLocked: boolean;
  pinLockedUntil: number | null;
  isInternalTeam: boolean;
  isEarlyAdopter: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isApiMode: boolean; // true = connected to real API
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  switchRole: (role: UserRole) => void;
  setUser: (user: User) => void;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  /** Refetches the caller's role for a specific store and updates it on the
   * current user — a user's role can differ per store, so the role fetched
   * once at login can't be reused across a store switch. */
  refetchRoleForStore: (storeId: string) => Promise<void>;
}

// ============================================================
// Mock Users (1 per role for testing/fallback)
// ============================================================

export const MOCK_USERS: User[] = [
  {
    id: "user-1",
    name: "Budi Santoso",
    email: "owner@kustomer.id",
    avatar: "BS",
    role: "owner",
    stores: [
      { storeId: "store-1", storeName: "Toko Utama", role: "owner", tier: "business" },
      { storeId: "store-2", storeName: "Toko Cabang Jakarta", role: "owner", tier: "starter" },
      { storeId: "store-3", storeName: "Toko Cabang Surabaya", role: "owner", tier: "starter" },
    ],
    hasPin: true,
    pinLocked: false,
    pinLockedUntil: null,
    isInternalTeam: true,
    isEarlyAdopter: false,
  },
  {
    id: "user-2",
    name: "Sarah Lee",
    email: "admin@kustomer.id",
    avatar: "SL",
    role: "admin",
    stores: [
      { storeId: "store-1", storeName: "Toko Utama", role: "admin", tier: "business" },
      { storeId: "store-2", storeName: "Toko Cabang Jakarta", role: "admin", tier: "starter" },
      { storeId: "store-3", storeName: "Toko Cabang Surabaya", role: "admin", tier: "starter" },
    ],
    hasPin: true,
    pinLocked: false,
    pinLockedUntil: null,
    isInternalTeam: true,
    isEarlyAdopter: false,
  },
  {
    id: "user-3",
    name: "Ahmad Yani",
    email: "marketing@kustomer.id",
    avatar: "AY",
    role: "marketing",
    stores: [
      { storeId: "store-1", storeName: "Toko Utama", role: "marketing", tier: "business" },
      { storeId: "store-2", storeName: "Toko Cabang Jakarta", role: "marketing", tier: "starter" },
    ],
    hasPin: false,
    pinLocked: false,
    pinLockedUntil: null,
    isInternalTeam: true,
    isEarlyAdopter: false,
  },
  {
    id: "user-4",
    name: "Dewi Putri",
    email: "agent@kustomer.id",
    avatar: "DP",
    role: "cs_agent",
    stores: [{ storeId: "store-1", storeName: "Toko Utama", role: "cs_agent", tier: "business" }],
    hasPin: false,
    pinLocked: false,
    pinLockedUntil: null,
    isInternalTeam: false,
    isEarlyAdopter: false,
  },
  {
    id: "user-5",
    name: "Andi Pratama",
    email: "finance@kustomer.id",
    avatar: "AP",
    role: "finance",
    stores: [
      { storeId: "store-1", storeName: "Toko Utama", role: "finance", tier: "business" },
      { storeId: "store-2", storeName: "Toko Cabang Jakarta", role: "finance", tier: "starter" },
      { storeId: "store-3", storeName: "Toko Cabang Surabaya", role: "finance", tier: "starter" },
    ],
    hasPin: true,
    pinLocked: false,
    pinLockedUntil: null,
    isInternalTeam: false,
    isEarlyAdopter: false,
  },
  {
    id: "user-6",
    name: "Reza Firmansyah",
    email: "dev@kustomer.id",
    avatar: "RF",
    role: "developer",
    stores: [{ storeId: "store-1", storeName: "Toko Utama", role: "developer", tier: "business" }],
    hasPin: false,
    pinLocked: false,
    pinLockedUntil: null,
    isInternalTeam: true,
    isEarlyAdopter: false,
  },
];

// ============================================================
// Helper: Map API response to User object
// ============================================================

function getStoresForRole(role: UserRole): StoreAccess[] {
  // Stores are assigned based on role level
  const allStores: StoreAccess[] = [
    { storeId: "store-1", storeName: "Toko Utama", role, tier: "business" },
    { storeId: "store-2", storeName: "Toko Cabang Jakarta", role, tier: "starter" },
    { storeId: "store-3", storeName: "Toko Cabang Surabaya", role, tier: "starter" },
  ];

  // Owner, Admin, Finance get all stores
  if (["owner", "admin", "finance"].includes(role)) {
    return allStores;
  }
  // Marketing gets 2 stores
  if (role === "marketing") {
    return allStores.slice(0, 2);
  }
  // CS Agent, Developer get 1 store
  return allStores.slice(0, 1);
}

/** After accept-invite or register: set JWT + user + stores without a second login request. */
export function applyLoginResponse(data: LoginApiResponse): void {
  const user = apiUserToUser(data.user, data.stores);
  setAuthToken(data.token);
  useAuthStore.setState({
    user,
    token: data.token,
    isAuthenticated: true,
    isLoading: false,
    isApiMode: true,
    error: null,
  });
  if (data.stores && data.stores.length > 0) {
    useStoreStore.getState().setStoresFromApiResponse(data.stores);
  }
}

/**
 * Switches the active store and refetches the caller's role for it.
 * Use this instead of useStoreStore's setActiveStore directly wherever the
 * store switch should affect what the user is allowed to do (permission
 * checks all read useAuthStore's user.role) — defined here rather than in
 * store-store.ts to avoid a circular import (auth-store already depends on
 * store-store for store hydration).
 */
export async function switchActiveStore(storeId: string): Promise<void> {
  useStoreStore.getState().setActiveStore(storeId);
  await useAuthStore.getState().refetchRoleForStore(storeId);
}

function apiUserToUser(
  apiUser: {
    id: string;
    email: string;
    full_name: string;
    role: string;
    avatar: string;
    is_internal_team?: boolean;
    is_early_adopter?: boolean;
  },
  apiStores?: ApiStoreDTO[]
): User {
  const role = apiUser.role as UserRole;

  // Map stores from API response if available, otherwise fall back to mock
  let stores: StoreAccess[];
  if (apiStores && apiStores.length > 0) {
    stores = apiStores.map((s) => ({
      storeId: s.id,
      storeName: s.name,
      role,
      tier: s.tier as StoreTier,
      category: s.category,
      description: s.description,
    }));
  } else {
    stores = getStoresForRole(role);
  }

  return {
    id: apiUser.id,
    name: apiUser.full_name,
    email: apiUser.email,
    avatar:
      apiUser.avatar ||
      apiUser.full_name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2),
    role,
    stores,
    hasPin: ["owner", "admin", "finance"].includes(role),
    pinLocked: false,
    pinLockedUntil: null,
    isInternalTeam: apiUser.is_internal_team ?? false,
    isEarlyAdopter: apiUser.is_early_adopter ?? false,
  };
}

// ============================================================
// Store
// ============================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      isApiMode: false,
      error: null,

      login: async (email: string, password: string) => {
        console.log("[Auth Store] 🔐 Login attempt started", {
          email,
          timestamp: new Date().toISOString(),
          mockFallbackEnabled: ENABLE_MOCK_FALLBACK,
        });

        set({ isLoading: true, error: null });

        try {
          // Try real API first
          console.log("[Auth Store] 🔍 Checking API reachability...");
          const apiReachable = await isApiReachable();

          console.log("[Auth Store] API reachability check:", {
            reachable: apiReachable,
            apiBaseUrl: process.env.NEXT_PUBLIC_API_BASE_URL,
          });

          if (apiReachable) {
            console.log("[Auth Store] ✅ API is reachable, attempting login...");

            try {
              const loginData = await apiLogin(email, password);
              const user = apiUserToUser(loginData.user, loginData.stores);

              console.log("[Auth Store] ✅ Login data processed, setting auth state");

              set({
                user,
                token: loginData.token,
                isAuthenticated: true,
                isLoading: false,
                isApiMode: true,
                error: null,
              });

              // Hydrate store-store with real stores from login response
              if (loginData.stores && loginData.stores.length > 0) {
                console.log("[Auth Store] 📦 Hydrating store-store with", loginData.stores.length, "stores");
                useStoreStore.getState().setStoresFromApiResponse(loginData.stores);
              }

              console.log("[Auth Store] ✅ Login berhasil via API backend");
              return true;
            } catch (apiErr) {
              const errMsg = apiErr instanceof Error ? apiErr.message : "Login gagal";
              const statusCode = apiErr instanceof ApiError ? apiErr.status : undefined;

              console.error("[Auth Store] ❌ API Login error:", {
                message: errMsg,
                statusCode,
                error: apiErr,
              });

              // Check for database error
              if (errMsg.includes("Database not available") || errMsg.includes("Database")) {
                console.error("[Auth Store] 🔴 Database connection error detected");
                set({
                  isLoading: false,
                  error: "Backend database tidak tersedia. Hubungi administrator.",
                });
                return false;
              }

              // If API returns auth error (wrong credentials), don't fall back to mock
              if (errMsg.includes("Invalid") || errMsg.includes("email") || errMsg.includes("password")) {
                console.warn("[Auth Store] ⚠️ Invalid credentials, not falling back to mock");
                set({ isLoading: false, error: "Email atau password salah" });
                return false;
              }

              // For other API errors, fall through to mock if enabled
              console.warn("[Auth Store] ⚠️ API error, checking mock fallback:", {
                message: errMsg,
                mockEnabled: ENABLE_MOCK_FALLBACK,
              });
            }
          } else {
            console.warn("[Auth Store] ⚠️ API not reachable, checking mock fallback");
          }

          // Mock fallback
          if (ENABLE_MOCK_FALLBACK) {
            console.log("[Auth] API tidak tersedia, menggunakan mock login");
            await new Promise((resolve) => setTimeout(resolve, 500));

            const foundUser = MOCK_USERS.find((u) => u.email === email);
            if (foundUser) {
              set({
                user: foundUser,
                token: null,
                isAuthenticated: true,
                isLoading: false,
                isApiMode: false,
                error: null,
              });
              return true;
            }

            // If email matches pattern, create default owner user
            if (email.includes("@")) {
              const defaultUser = { ...MOCK_USERS[0], email, name: email.split("@")[0] };
              set({
                user: defaultUser,
                token: null,
                isAuthenticated: true,
                isLoading: false,
                isApiMode: false,
                error: null,
              });
              return true;
            }
          } else {
            console.error("[Auth] ❌ Mock fallback is DISABLED. API Mode ONLY.");
            console.error("[Auth] Backend must be running at:", process.env.NEXT_PUBLIC_API_BASE_URL);
          }

          set({
            isLoading: false,
            error: ENABLE_MOCK_FALLBACK
              ? "Tidak dapat terhubung ke server"
              : "Backend API tidak tersedia. Pastikan backend berjalan di " + process.env.NEXT_PUBLIC_API_BASE_URL,
          });
          return false;
        } catch (err) {
          console.error("[Auth] Login error:", err);
          set({
            isLoading: false,
            error: err instanceof Error ? err.message : "Terjadi kesalahan",
          });
          return false;
        }
      },

      logout: () => {
        apiLogout();
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          isApiMode: false,
          error: null,
        });
      },

      switchRole: (role: UserRole) => {
        const currentUser = get().user;
        if (!currentUser) return;

        // Find mock user for this role
        const roleUser = MOCK_USERS.find((u) => u.role === role);
        if (roleUser) {
          set({ user: { ...roleUser } });
        }
      },

      setUser: (user: User) => {
        set({ user, isAuthenticated: true });
      },

      checkAuth: async () => {
        const token = getAuthToken();
        if (!token) return;

        try {
          const meData = await apiGetMe();
          // /auth/me tidak membawa daftar toko (beda dari /auth/login) — ambil
          // terpisah. Kegagalan di sini tidak boleh membatalkan sesi yang
          // tokennya sudah terbukti sah; apiUserToUser jatuh ke mock kalau
          // apiStores kosong, sama seperti sebelumnya.
          const apiStores = await getMyStores().catch(() => undefined);
          const user = apiUserToUser(meData, apiStores);
          set({
            user,
            token,
            isAuthenticated: true,
            isApiMode: true,
          });
          if (apiStores && apiStores.length > 0) {
            useStoreStore.getState().setStoresFromApiResponse(apiStores);
          }
        } catch {
          // Token expired or invalid
          apiLogout();
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isApiMode: false,
          });
        }
      },

      clearError: () => {
        set({ error: null });
      },

      refetchRoleForStore: async (storeId: string) => {
        const currentUser = get().user;
        if (!currentUser) return;
        try {
          const role = await getMyRole(storeId);
          const updatedUser = get().user;
          if (!updatedUser) return;
          set({ user: { ...updatedUser, role: role as UserRole } });
        } catch (err) {
          console.warn("[Auth Store] Failed to refetch role for store", storeId, err);
        }
      },
    }),
    {
      name: "kustomer-auth",
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        isApiMode: state.isApiMode,
      }),
      onRehydrateStorage: () => (state) => {
        setAuthToken(state?.token ?? null);
      },
    }
  )
);
